import React from 'react';
import { Autocomplete, AutocompleteProps, CircularProgress, debounce, TextField } from '@mui/material';

type IFetchAutocompleteProps<
    T,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
> = {
    fetchRequest: (request: { searchText: string }) => Promise<T[]>,
    textFieldProps?: any,
} & Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'options' | 'renderInput'>

export const FetchAutocomplete = <
    T,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
>({ value, fetchRequest, textFieldProps, onChange, ...autocompleteProps }: IFetchAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {


    const [open, setOpen] = React.useState(false);
    const [searchText, setSearchText] = React.useState('');
    const [optionList, setOptionList] = React.useState<readonly T[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const fetchOptionList = React.useMemo(
        () =>
            debounce(
                (
                    request: { searchText: string },
                    callback: (results?: readonly T[]) => void,
                ) => {
                    fetchRequest(request).then(callback);
                },
                400,
            ),
        [fetchRequest],
    );

    React.useEffect(() => {
        let active = true;
        setIsLoading(true);


        fetchOptionList({ searchText }, (results?: readonly T[]) => {
            if (active) {
                let newOptions: readonly T[] = [];

                if (value) {
                    newOptions = [value as T]; //TODO: check it for multiple mode
                }

                if (results && autocompleteProps.isOptionEqualToValue) {
                    const optionEqualToValue = autocompleteProps.isOptionEqualToValue
                        ? autocompleteProps.isOptionEqualToValue
                        : (option: any, value: any) => option.id === value.id;

                    newOptions = [
                        ...newOptions,
                        ...(value
                            ? results.filter(r => !optionEqualToValue(r, value as any))
                            : results),
                    ];
                }

                setOptionList(newOptions);
                setIsLoading(false);
            }
        });

        return () => {
            active = false;
        };

    }, [value, searchText]);


    return (
        <Autocomplete
            value={value}
            onChange={(event, newValue, reason, details) => {
                onChange && onChange(event, newValue, reason, details);
            }}
            onInputChange={(event, newInputValue) => {
                setSearchText(newInputValue);
            }}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            loading={open && isLoading}
            loadingText='Загрузка...'
            filterOptions={(x) => x}
            options={optionList}
            getOptionLabel={(option) => option.name}
            autoComplete
            includeInputInList
            size='small'
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            sx={{
                height: '38px',
                maxWidth: '300px',
                width: '100%',
            }}
            renderInput={
                params =>
                    <TextField
                        {...params}
                        {...textFieldProps}
                        InputProps={{
                            ...params.InputProps, //TODO Проверить, возможно надо заменить на textFieldProps.InputProps
                            placeholder: textFieldProps?.InputProps?.placeholder,
                            endAdornment: (
                                <>
                                    {(open && isLoading) ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
            }
            {...autocompleteProps}
        />
    );
}